import { useState, useEffect, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import { useNavPanelStore } from '../../stores/navPanelStore'
import {
  WERKBLAD_PREDIKAAT, VERLICHTING_PREDIKAAT, BESLAG_PREDIKAAT,
  SANITAIR_PREDIKAAT, PLINTEN_PREDIKAAT, DRESSING_PREDIKAAT, GREPEN_PREDIKAAT,
} from '../../utils/materiaalGroep'
import { MaterialQuickAdd } from '../quickAdd/MaterialQuickAdd'
import { CustomerQuickAdd } from '../quickAdd/CustomerQuickAdd'
import ProductinformatieBlok from '../Common/ProductinformatieBlok.jsx'
import { Package, Users, Briefcase, Refrigerator, Layers, Lightbulb, Wrench, Droplet, PanelBottom, Shirt, Grip, Combine, Search, X, Maximize2, Minimize2, Loader2, Plus, ArrowLeft, ExternalLink, Monitor } from 'lucide-react'

/**
 * ContextPanel — actief contextueel zijpaneel (Sprint A, laag 2/3).
 *
 * Drie tabs (Materialen, Klanten, Projecten). Per tab: zoeken, toevoegen (quick-add),
 * een rij openen (detail IN het paneel), en doorklikken naar het volledige scherm.
 * Hergebruikt de bestaande react-query calls (gedeelde cache met de hoofdschermen).
 *
 * Props:
 *   schermtype          : string   — route-prefix; bepaalt welke store-state gebruikt wordt
 *   highlightMaterialId : id|null  — scrollt + licht het materiaal kort op (en opent de materialen-tab)
 *   onUseMateriaal      : (mat) => void  — optioneel: materiaal in de actieve offerterij gebruiken
 */
const TABS = [
  { id: 'materialen', label: 'Materialen', icon: Package },
  { id: 'werkbladen', label: 'Werkbladen', icon: Layers },
  { id: 'verlichting', label: 'Verlichting', icon: Lightbulb },
  { id: 'beslag', label: 'Beslag', icon: Wrench },
  { id: 'sanitair', label: 'Sanitair', icon: Droplet },
  { id: 'plinten', label: 'Plinten', icon: PanelBottom },
  { id: 'dressing', label: 'Dressing', icon: Shirt },
  { id: 'grepen', label: 'Grepen', icon: Grip },
  { id: 'composieten', label: 'Samengesteld', icon: Combine },
  { id: 'klanten', label: 'Klanten', icon: Users },
  { id: 'projecten', label: 'Projecten', icon: Briefcase },
  { id: 'apparaten', label: 'Apparaten', icon: Refrigerator },
]

// Cluster-tab-predicaten (gedeeld met de drag-and-drop-routing in OfferteFichePage,
// zodat paneel-cluster en offerte-sectie altijd overeenkomen).

// Lookup-/referentie-tabs: geen quick-add-knop (alleen filteren/openen/gebruiken).
const LOOKUP_TABS = new Set(['werkbladen', 'verlichting', 'beslag', 'sanitair', 'plinten', 'dressing', 'grepen', 'composieten', 'apparaten'])

export default function ContextPanel({ schermtype, highlightMaterialId, onPopout, isPopped, onReturn }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { getStateForScreen, setActiveTab, setPanelOpen, setMode, setHighlightMaterial } = useNavPanelStore()
  // "Gebruik in offerte": handler die de actieve offerte-tab registreert (null = knop verbergen).
  const useMateriaal = useNavPanelStore((s) => s.useMateriaalHandler)
  const screen = getStateForScreen(schermtype)
  const activeTab = screen.activeTab
  const mode = screen.mode
  const [zoek, setZoek] = useState('')
  const [detail, setDetail] = useState(null)   // { tab, id }
  const [addOpen, setAddOpen] = useState(false) // quick-add modal voor de actieve tab

  // Een ↗-verzoek vanuit een offerterij opent het materiaal direct in detail.
  useEffect(() => {
    if (highlightMaterialId == null) return
    setActiveTab(schermtype, 'materialen')
    setDetail({ tab: 'materialen', id: highlightMaterialId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightMaterialId])

  // Zoekterm + detail resetten bij tabwissel.
  useEffect(() => { setZoek(''); setDetail(null) }, [activeTab])

  const openDetail = (tab, id) => setDetail({ tab, id })
  const handleAddClick = () => {
    if (activeTab === 'projecten') navigate('/projecten')
    else setAddOpen(true)
  }

  return (
    <div className="flex flex-col h-full bg-surface dark:bg-gray-800 border-l border-line dark:border-gray-700">
      {/* Header: tabs + acties */}
      <div className="flex items-center justify-between gap-2 px-2 h-12 border-b border-line dark:border-gray-700 shrink-0">
        <div className="flex items-center gap-0.5 flex-1 min-w-0 overflow-x-auto scrollbar-none">
          {TABS.map((tabDef) => {
            const Icon = tabDef.icon
            const actief = activeTab === tabDef.id
            return (
              <button
                key={tabDef.id}
                type="button"
                onClick={() => setActiveTab(schermtype, tabDef.id)}
                title={tabDef.label}
                className={`inline-flex items-center gap-1.5 shrink-0 px-2.5 py-1.5 rounded-control text-xs font-semibold transition ${actief ? 'bg-accent/10 text-accent' : 'text-muted hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden xl:inline">{tabDef.label}</span>
              </button>
            )
          })}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {isPopped ? (
            <button
              type="button"
              onClick={onReturn}
              title="Terug naar hoofdvenster"
              className="inline-flex items-center gap-1 px-2 py-1.5 rounded-control text-muted hover:text-ink hover:bg-gray-100 dark:hover:bg-gray-700 text-xs font-semibold"
            >
              <ArrowLeft className="w-4 h-4" /> Terug
            </button>
          ) : (
            <>
              {onPopout && (
                <button
                  type="button"
                  onClick={onPopout}
                  title="Naar tweede scherm (los venster)"
                  className="p-1.5 rounded-control text-muted hover:text-ink hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <Monitor className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setMode(schermtype, mode === 3 ? 2 : 3)}
                title={mode === 3 ? 'Inklappen tot zijpaneel' : 'Uitvouwen tot splitscreen'}
                className="p-1.5 rounded-control text-muted hover:text-ink hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {mode === 3 ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={() => setPanelOpen(schermtype, false)}
                title="Paneel sluiten"
                className="p-1.5 rounded-control text-muted hover:text-ink hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {detail ? (
        <DetailView
          tab={detail.tab}
          id={detail.id}
          onBack={() => setDetail(null)}
          navigate={navigate}
          onUseMateriaal={useMateriaal}
        />
      ) : (
        <>
          {/* Zoekbalk + toevoegen */}
          <div className="px-3 py-2 border-b border-line dark:border-gray-700 shrink-0 flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-muted absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                value={zoek}
                onChange={(e) => setZoek(e.target.value)}
                placeholder="Zoeken…"
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-line dark:border-gray-600 rounded-control bg-canvas dark:bg-gray-700 dark:text-white"
              />
            </div>
            {/* Referentie-/lookuptabs: geen quick-add hier */}
            {!LOOKUP_TABS.has(activeTab) && (
              <button
                type="button"
                onClick={handleAddClick}
                title={activeTab === 'projecten' ? 'Naar projecten' : 'Nieuw toevoegen'}
                className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-control bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold"
              >
                <Plus className="w-4 h-4" /> {activeTab === 'projecten' ? 'Projecten' : 'Nieuw'}
              </button>
            )}
          </div>

          {/* Inhoud */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {activeTab === 'materialen' && (
              <MaterialenLijst zoek={zoek} highlightMaterialId={highlightMaterialId} onUse={useMateriaal} onOpen={(id) => openDetail('materialen', id)} />
            )}
            {activeTab === 'werkbladen' && (
              <MaterialenLijst zoek={zoek} onUse={useMateriaal} onOpen={(id) => openDetail('materialen', id)} groepPredikaat={WERKBLAD_PREDIKAAT} />
            )}
            {activeTab === 'verlichting' && (
              <MaterialenLijst zoek={zoek} onUse={useMateriaal} onOpen={(id) => openDetail('materialen', id)} groepPredikaat={VERLICHTING_PREDIKAAT} />
            )}
            {activeTab === 'beslag' && (
              <MaterialenLijst zoek={zoek} onUse={useMateriaal} onOpen={(id) => openDetail('materialen', id)} groepPredikaat={BESLAG_PREDIKAAT} />
            )}
            {activeTab === 'sanitair' && (
              <MaterialenLijst zoek={zoek} onUse={useMateriaal} onOpen={(id) => openDetail('materialen', id)} groepPredikaat={SANITAIR_PREDIKAAT} />
            )}
            {activeTab === 'plinten' && (
              <MaterialenLijst zoek={zoek} onUse={useMateriaal} onOpen={(id) => openDetail('materialen', id)} groepPredikaat={PLINTEN_PREDIKAAT} />
            )}
            {activeTab === 'dressing' && (
              <MaterialenLijst zoek={zoek} onUse={useMateriaal} onOpen={(id) => openDetail('materialen', id)} groepPredikaat={DRESSING_PREDIKAAT} />
            )}
            {activeTab === 'grepen' && (
              <MaterialenLijst zoek={zoek} onUse={useMateriaal} onOpen={(id) => openDetail('materialen', id)} groepPredikaat={GREPEN_PREDIKAAT} />
            )}
            {activeTab === 'composieten' && (
              <SamengesteldLijst
                zoek={zoek}
                onOpen={(id) => navigate(`/materialen?composiet=${id}`)}
              />
            )}
            {activeTab === 'klanten' && <KlantenLijst zoek={zoek} onOpen={(id) => openDetail('klanten', id)} />}
            {activeTab === 'projecten' && <ProjectenLijst zoek={zoek} onOpen={(id) => openDetail('projecten', id)} />}
            {activeTab === 'apparaten' && <ApparatenLijst zoek={zoek} />}
          </div>
        </>
      )}

      {/* Quick-add modals (hergebruik bestaande componenten) */}
      {addOpen && activeTab === 'materialen' && (
        <MaterialQuickAdd
          open
          onClose={() => setAddOpen(false)}
          onCreated={(m) => {
            setAddOpen(false)
            qc.invalidateQueries({ queryKey: ['materialen'] })
            if (m?.id != null) { setActiveTab(schermtype, 'materialen'); setHighlightMaterial(m.id) }
          }}
        />
      )}
      {addOpen && activeTab === 'klanten' && (
        <CustomerQuickAdd
          open
          onClose={() => setAddOpen(false)}
          onCreated={(k) => {
            setAddOpen(false)
            qc.invalidateQueries({ queryKey: ['klanten'] })
            if (k?.id != null) openDetail('klanten', k.id)
          }}
        />
      )}
    </div>
  )
}

function LijstStatus({ isLoading, leeg, leegTekst }) {
  if (isLoading) return <div className="p-6 text-center text-muted"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
  if (leeg) return <div className="p-6 text-sm text-muted">{leegTekst}</div>
  return null
}

function MaterialenLijst({ zoek, highlightMaterialId, onUse, onOpen, groepPredikaat }) {
  const [groepId, setGroepId] = useState('')   // '' = Alle, anders materiaalgroep-id
  const { data: materialen = [], isLoading } = useQuery({
    queryKey: ['materialen'],
    queryFn: async () => (await api.get('/materialen', { params: { limit: 1000, is_actief: true } })).data || [],
  })
  // Groep-namen via de bestaande catalogus (de /materialen-response bevat enkel de id's).
  const { data: groepen = [] } = useQuery({
    queryKey: ['materiaal-groepen'],
    queryFn: async () => (await api.get('/materiaal-groepen')).data || [],
    staleTime: 5 * 60 * 1000,
  })
  const rowRefs = useRef(new Map())
  // Drag-and-drop naar de offerte-werkruimte (alleen zinvol als er een "Gebruik"-handler is).
  const setDraggingMateriaal = useNavPanelStore((s) => s.setDraggingMateriaal)
  const draggingId = useNavPanelStore((s) => s.draggingMateriaal?.id)

  // Materiaal hoort bij een groep via primaire id of de many-to-many lijst.
  const inGroep = (m, gid) =>
    m.materiaal_groep_id === gid || (Array.isArray(m.materiaal_groep_ids) && m.materiaal_groep_ids.includes(gid))

  // Optionele scope (bv. Werkbladen-tab): enkel groepen die aan het predikaat voldoen.
  const relevanteIds = useMemo(() => {
    if (!groepPredikaat) return null
    return new Set(groepen.filter(groepPredikaat).map((g) => g.id))
  }, [groepen, groepPredikaat])
  const inScope = (m) => {
    if (!relevanteIds) return true
    if (m.materiaal_groep_id != null && relevanteIds.has(m.materiaal_groep_id)) return true
    if (Array.isArray(m.materiaal_groep_ids) && m.materiaal_groep_ids.some((id) => relevanteIds.has(id))) return true
    return false
  }

  // Chips: alleen de (in-scope) groepen die echt voorkomen in de geladen materialen (Alle + aanwezige).
  const chips = useMemo(() => {
    const aanwezig = new Set()
    for (const m of materialen) {
      if (!inScope(m)) continue
      if (m.materiaal_groep_id != null) aanwezig.add(m.materiaal_groep_id)
      if (Array.isArray(m.materiaal_groep_ids)) m.materiaal_groep_ids.forEach((id) => aanwezig.add(id))
    }
    const lijst = groepen
      .filter((g) => aanwezig.has(g.id) && (!relevanteIds || relevanteIds.has(g.id)))
      .sort((a, b) => (a.naam || '').localeCompare(b.naam || ''))
    return [{ value: '', label: 'Alle' }, ...lijst.map((g) => ({ value: g.id, label: g.naam || `Groep ${g.id}` }))]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialen, groepen, relevanteIds])

  const gefilterd = useMemo(() => {
    const term = zoek.trim().toLowerCase()
    return materialen.filter((m) => {
      if (!inScope(m)) return false
      if (groepId !== '' && !inGroep(m, groepId)) return false
      if (term) {
        const code = (m.interne_code || m.sku || '').toLowerCase()
        if (!((m.naam || '').toLowerCase().includes(term) || code.includes(term))) return false
      }
      return true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialen, zoek, groepId, relevanteIds])

  useEffect(() => {
    if (highlightMaterialId == null) return
    const el = rowRefs.current.get(String(highlightMaterialId))
    if (!el) return
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    el.classList.add('ring-2', 'ring-accent', 'ring-inset')
    const tmo = setTimeout(() => el.classList.remove('ring-2', 'ring-accent', 'ring-inset'), 1800)
    return () => clearTimeout(tmo)
  }, [highlightMaterialId, materialen])

  const prijs = (m) => m.prijs_verkoop_per_eenheid ?? m.prijs_per_eenheid ?? null
  return (
    <div>
      {/* Chips per materiaalgroep. Bij een scope (cluster-tab) alleen tonen als er
          echt meerdere subgroepen zijn — anders is "Alle / <enige groep>" ruis. */}
      {(groepPredikaat ? chips.length > 2 : chips.length > 1) && (
        <div className="flex flex-wrap gap-1.5 px-3 py-2 border-b border-line dark:border-gray-700 sticky top-0 bg-surface dark:bg-gray-800 z-10">
          {chips.map((c) => (
            <button
              key={c.value}
              onClick={() => setGroepId(c.value)}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${groepId === c.value ? 'bg-accent/10 text-accent border-accent/30' : 'border-line dark:border-gray-600 text-muted hover:bg-gray-50 dark:hover:bg-gray-700'}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}
      {(isLoading || gefilterd.length === 0) ? (
        <LijstStatus isLoading={isLoading} leeg={gefilterd.length === 0} leegTekst="Geen materialen gevonden." />
      ) : (
        <ul className="divide-y divide-line dark:divide-gray-700">
          {gefilterd.map((m) => (
            <li
              key={m.id}
              ref={(el) => { if (el) rowRefs.current.set(String(m.id), el); else rowRefs.current.delete(String(m.id)) }}
              onClick={() => onOpen?.(m.id)}
              draggable={!!onUse}
              onDragStart={onUse ? (e) => {
                e.dataTransfer.setData('application/x-orion-materiaal', JSON.stringify(m))
                e.dataTransfer.setData('text/plain', m.naam || '')
                e.dataTransfer.effectAllowed = 'copy'
                setDraggingMateriaal(m)
              } : undefined}
              onDragEnd={onUse ? () => setDraggingMateriaal(null) : undefined}
              title={onUse ? 'Sleep naar de offerte om toe te voegen — of klik om te openen' : undefined}
              className={`px-3 py-2 flex items-start justify-between gap-2 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer ${onUse ? 'active:cursor-grabbing' : ''} ${draggingId === m.id ? 'opacity-50' : ''}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-ink dark:text-white truncate">
                  {(m.interne_code || m.sku) ? <span className="font-mono text-xs text-muted mr-1">{m.interne_code || m.sku}</span> : null}
                  {m.naam}
                </div>
                <div className="text-xs text-muted flex flex-wrap gap-x-2">
                  {prijs(m) != null && <span>€ {Number(prijs(m)).toFixed(2)}/{m.eenheid || 'st'}</span>}
                  {m.stock_quantity != null && <span>voorraad: {m.stock_quantity}</span>}
                </div>
              </div>
              {onUse && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onUse(m) }}
                  className="shrink-0 text-xs font-semibold px-2 py-1 rounded-control bg-accent/10 text-accent hover:bg-accent/20"
                >
                  Gebruik
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function KlantenLijst({ zoek, onOpen }) {
  const { data: klanten = [], isLoading } = useQuery({
    queryKey: ['klanten', true],
    queryFn: async () => {
      try { return (await api.get('/klanten', { params: { is_actief: true } })).data || [] } catch { return [] }
    },
    staleTime: 30000,
  })
  const gefilterd = useMemo(() => {
    const term = zoek.trim().toLowerCase()
    if (!term) return klanten
    return klanten.filter((k) => `${k.naam || ''} ${k.bedrijfsnaam || ''} ${k.email || ''}`.toLowerCase().includes(term))
  }, [klanten, zoek])

  if (isLoading || gefilterd.length === 0) {
    return <LijstStatus isLoading={isLoading} leeg={gefilterd.length === 0} leegTekst="Geen klanten gevonden." />
  }
  return (
    <ul className="divide-y divide-line dark:divide-gray-700">
      {gefilterd.map((k) => (
        <li key={k.id} onClick={() => onOpen?.(k.id)} className="px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer">
          <div className="text-sm font-medium text-ink dark:text-white truncate">{k.bedrijfsnaam || k.naam || `Klant #${k.id}`}</div>
          <div className="text-xs text-muted truncate">{[k.naam, k.email, k.telefoon].filter(Boolean).join(' · ') || '—'}</div>
        </li>
      ))}
    </ul>
  )
}

function ProjectenLijst({ zoek, onOpen }) {
  const { data: projecten = [], isLoading } = useQuery({
    queryKey: ['projecten', 'B2B'],
    queryFn: async () => (await api.get('/projecten?segment_type=B2B')).data || [],
  })
  const gefilterd = useMemo(() => {
    const term = zoek.trim().toLowerCase()
    if (!term) return projecten
    return projecten.filter((p) => `${p.naam || ''} ${p.projectnummer || ''} ${p.status || ''}`.toLowerCase().includes(term))
  }, [projecten, zoek])

  if (isLoading || gefilterd.length === 0) {
    return <LijstStatus isLoading={isLoading} leeg={gefilterd.length === 0} leegTekst="Geen projecten gevonden." />
  }
  return (
    <ul className="divide-y divide-line dark:divide-gray-700">
      {gefilterd.map((p) => (
        <li key={p.id} onClick={() => onOpen?.(p.id)} className="px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-medium text-ink dark:text-white truncate">{p.naam || `Project #${p.id}`}</div>
            {p.status && <span className="badge badge-concept shrink-0">{p.status}</span>}
          </div>
          {p.projectnummer && <div className="text-xs text-muted font-mono">{p.projectnummer}</div>}
        </li>
      ))}
    </ul>
  )
}

// Toestellentypes-catalogus (ApparaatType) met filter per type + zoek op naam.
// Toont de niche-afmetingen — handig bij het inplannen in een kast.
// Bekende categorie-waarden -> nette labels; koel/vries/wijn vallen samen onder "Koeling".
const APP_CAT_LABELS = {
  oven: 'Oven', stoomoven: 'Stoomoven', combi: 'Combi-oven', magnetron: 'Magnetron',
  warmhoudlade: 'Warmhoudlade', koffie: 'Koffie',
  koelkast_hoog: 'Koeling', koelkast_onder: 'Koeling', vriezer_hoog: 'Koeling', wijnklimaatkast: 'Koeling',
  vaatwasmachine: 'Vaatwas', dampkap: 'Dampkap',
}
const labelVoor = (cat) => APP_CAT_LABELS[cat]
  || String(cat || 'overige').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())

function ApparatenLijst({ zoek }) {
  const [catLabel, setCatLabel] = useState('')
  const { data: types = [], isLoading } = useQuery({
    queryKey: ['apparaat-types'],
    queryFn: async () => (await api.get('/apparaat-types')).data || [],
    staleTime: 5 * 60 * 1000,
  })

  // Chips afgeleid uit de werkelijk aanwezige type-labels (Alle + aanwezige), gesorteerd.
  const chips = useMemo(() => {
    const labels = []
    for (const t of types) {
      const l = labelVoor(t.categorie)
      if (!labels.includes(l)) labels.push(l)
    }
    labels.sort((a, b) => a.localeCompare(b))
    return [{ value: '', label: 'Alle' }, ...labels.map((l) => ({ value: l, label: l }))]
  }, [types])

  const gefilterd = useMemo(() => {
    const term = zoek.trim().toLowerCase()
    return types.filter((t) => {
      if (catLabel && labelVoor(t.categorie) !== catLabel) return false
      if (term && !(t.naam || '').toLowerCase().includes(term)) return false
      return true
    })
  }, [types, zoek, catLabel])

  const nis = (t) => {
    const b = t.nisbreedte_min_mm && t.nisbreedte_max_mm && t.nisbreedte_min_mm !== t.nisbreedte_max_mm
      ? `${t.nisbreedte_min_mm}–${t.nisbreedte_max_mm}` : (t.nisbreedte_min_mm || t.nisbreedte_max_mm || null)
    if (!b && !t.nishoogte_mm) return null
    return `nis ${b || '?'} × ${t.nishoogte_mm || '?'} mm`
  }

  return (
    <div>
      {/* Categorie-chips */}
      {chips.length > 1 && (
        <div className="flex flex-wrap gap-1.5 px-3 py-2 border-b border-line dark:border-gray-700 sticky top-0 bg-surface dark:bg-gray-800 z-10">
          {chips.map((c) => (
            <button
              key={c.value}
              onClick={() => setCatLabel(c.value)}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${catLabel === c.value ? 'bg-accent/10 text-accent border-accent/30' : 'border-line dark:border-gray-600 text-muted hover:bg-gray-50 dark:hover:bg-gray-700'}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}
      {(isLoading || gefilterd.length === 0) ? (
        <LijstStatus isLoading={isLoading} leeg={gefilterd.length === 0} leegTekst="Geen apparaat-types gevonden." />
      ) : (
        <ul className="divide-y divide-line dark:divide-gray-700">
          {gefilterd.map((t) => (
            <li key={t.id} className="px-3 py-2 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-ink dark:text-white truncate">
                  {t.icoon ? <span className="mr-1">{t.icoon}</span> : null}{t.naam}
                </div>
                {nis(t) && <div className="text-xs text-muted">{nis(t)}</div>}
              </div>
              <span className="badge badge-concept shrink-0">{labelVoor(t.categorie)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// Samengestelde materialen (MaterialComposite): zelf opgebouwde platen (drager + laminaat/fineer).
// Referentie-/lookuplijst — toont opbouw + berekende verkoopprijs per m² (single-point marge).
function SamengesteldLijst({ zoek, onOpen }) {
  const { data: composieten = [], isLoading } = useQuery({
    queryKey: ['material-matrix-composites'],
    queryFn: async () => (await api.get('material-matrix/composite')).data || [],
    staleTime: 5 * 60 * 1000,
  })

  const gefilterd = useMemo(() => {
    const term = zoek.trim().toLowerCase()
    if (!term) return composieten
    return composieten.filter((c) =>
      `${c.display_naam || ''} ${c.composite_code || ''} ${c.plaat_build_up_naam || ''}`.toLowerCase().includes(term))
  }, [composieten, zoek])

  if (isLoading || gefilterd.length === 0) {
    return <LijstStatus isLoading={isLoading} leeg={gefilterd.length === 0} leegTekst="Geen samengestelde materialen gevonden." />
  }
  return (
    <ul className="divide-y divide-line dark:divide-gray-700">
      {gefilterd.map((c) => (
        <li
          key={c.id}
          // Openen naar de hoofdmaterialen (recept-detail). Deze rijen waren platte <li>'s:
          // een samengesteld materiaal was daardoor niet te openen vanuit het paneel.
          onClick={() => onOpen?.(c.id)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen?.(c.id) } }}
          role={onOpen ? 'button' : undefined}
          tabIndex={onOpen ? 0 : undefined}
          title={onOpen ? 'Openen in Materialen' : undefined}
          className={`px-3 py-2 flex items-start justify-between gap-2 ${
            onOpen ? 'cursor-pointer hover:bg-canvas dark:hover:bg-gray-700/50 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-inset' : ''
          }`}
        >
          <div className="min-w-0">
            <div className="text-sm font-medium text-ink dark:text-white truncate">
              {c.composite_code ? <span className="font-mono text-xs text-muted mr-1">{c.composite_code}</span> : null}
              {c.display_naam || c.composite_code}
            </div>
            <div className="text-xs text-muted flex flex-wrap gap-x-2">
              {c.totaal_dikte_mm != null && <span>{Number(c.totaal_dikte_mm).toFixed(1)} mm</span>}
              {c.plaat_build_up_naam && <span>{c.plaat_build_up_naam}</span>}
              {c.grain_valid === false && <span className="text-amber-600 dark:text-amber-400">⚠ nerf</span>}
            </div>
          </div>
          {c.composite_price_per_m2 != null && (
            <span className="shrink-0 text-xs font-semibold px-2 py-1 rounded-control bg-accent/10 text-accent">
              € {Number(c.composite_price_per_m2).toFixed(2)}/m²
            </span>
          )}
        </li>
      ))}
    </ul>
  )
}

// ── Detail-weergave IN het paneel ─────────────────────────────────────────
function Veld({ label, children }) {
  if (children == null || children === '' ) return null
  return <div><dt className="text-xs text-muted">{label}</dt><dd className="text-sm text-ink dark:text-white break-words">{children}</dd></div>
}

function DetailView({ tab, id, onBack, navigate, onUseMateriaal }) {
  const endpoint = tab === 'materialen' ? `/materialen/${id}` : tab === 'klanten' ? `/klanten/${id}` : `/projecten/${id}`
  const { data, isLoading, isError } = useQuery({
    queryKey: [`${tab}-detail`, id],
    queryFn: async () => (await api.get(endpoint)).data,
    enabled: id != null,
    staleTime: 30000,
  })

  const titel = tab === 'materialen' ? 'Materiaal' : tab === 'klanten' ? 'Klant' : 'Project'

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-line dark:border-gray-700 shrink-0">
        <button type="button" onClick={onBack} title="Terug naar lijst" className="p-1.5 rounded-control text-muted hover:text-ink hover:bg-gray-100 dark:hover:bg-gray-700">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <span className="text-sm font-semibold text-ink dark:text-white">{titel}</span>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        {isLoading && <div className="text-sm text-muted flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Laden…</div>}
        {isError && <div className="text-sm text-amber-700 dark:text-amber-400">Kon detail niet laden.</div>}
        {data && tab === 'materialen' && <MateriaalDetail mat={data} onUseMateriaal={onUseMateriaal} navigate={navigate} />}
        {data && tab === 'klanten' && <KlantDetail klant={data} navigate={navigate} />}
        {data && tab === 'projecten' && <ProjectDetail project={data} navigate={navigate} />}
      </div>
    </div>
  )
}

function MateriaalDetail({ mat, onUseMateriaal, navigate }) {
  const prijsV = mat.prijs_verkoop_per_eenheid ?? mat.prijs_per_eenheid
  return (
    <div className="space-y-4">
      <div>
        <div className="text-base font-semibold text-ink dark:text-white">{mat.naam}</div>
        {(mat.interne_code || mat.sku) && <div className="font-mono text-xs text-muted">{mat.interne_code || mat.sku}</div>}
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
        <Veld label="Verkoop">{prijsV != null ? `€ ${Number(prijsV).toFixed(2)} / ${mat.eenheid || 'st'}` : null}</Veld>
        <Veld label="Aankoop">{mat.prijs_aankoop_per_eenheid != null ? `€ ${Number(mat.prijs_aankoop_per_eenheid).toFixed(2)}` : null}</Veld>
        <Veld label="Voorraad">{mat.stock_quantity != null ? `${mat.stock_quantity}${mat.min_stock_level != null ? ` (min. ${mat.min_stock_level})` : ''}` : null}</Veld>
        <Veld label="Eenheid">{mat.eenheid}</Veld>
        <Veld label="Leverancier">{mat.leverancier_naam}</Veld>
        <Veld label="Dikte">{mat.dikte_mm != null ? `${mat.dikte_mm} mm` : null}</Veld>
      </dl>
      {/* Wat de leverancier via de Hub deelt. Toont niets bij een eigen materiaal. */}
      <ProductinformatieBlok eigenschappen={mat.eigenschappen} />
      <div className="flex flex-wrap gap-2 pt-2 border-t border-line dark:border-gray-700">
        {onUseMateriaal && (
          <button type="button" onClick={() => onUseMateriaal(mat)} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-control bg-blue-600 hover:bg-blue-700 text-white">
            Gebruik in offerte
          </button>
        )}
        <button type="button" onClick={() => navigate(`/materialen?materiaal=${mat.id}`)} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-control bg-accent/10 text-accent hover:bg-accent/20">
          <ExternalLink className="w-3.5 h-3.5" /> Open in materialenbeheer
        </button>
      </div>
    </div>
  )
}

function KlantDetail({ klant, navigate }) {
  const adres = [klant.straat, [klant.postcode, klant.stad].filter(Boolean).join(' '), klant.land].filter(Boolean).join(', ')
  return (
    <div className="space-y-4">
      <div className="text-base font-semibold text-ink dark:text-white">{klant.bedrijfsnaam || klant.naam || `Klant #${klant.id}`}</div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
        <Veld label="Contact">{klant.naam}</Veld>
        <Veld label="E-mail">{klant.email}</Veld>
        <Veld label="Telefoon">{klant.telefoon}</Veld>
        <Veld label="BTW">{klant.btw_nummer}</Veld>
        <div className="col-span-2"><Veld label="Adres">{adres || null}</Veld></div>
      </dl>
      <div className="pt-2 border-t border-line dark:border-gray-700">
        <button type="button" onClick={() => navigate(`/klanten/${klant.id}`)} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-control bg-accent/10 text-accent hover:bg-accent/20">
          <ExternalLink className="w-3.5 h-3.5" /> Open klantdossier
        </button>
      </div>
    </div>
  )
}

function ProjectDetail({ project, navigate }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="text-base font-semibold text-ink dark:text-white">{project.naam || `Project #${project.id}`}</div>
        {project.status && <span className="badge badge-concept shrink-0">{project.status}</span>}
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
        <Veld label="Projectnummer">{project.projectnummer}</Veld>
        <Veld label="Klant">{project.klant_naam || project.klant?.naam}</Veld>
        <Veld label="Segment">{project.segment_type}</Veld>
      </dl>
      <div className="pt-2 border-t border-line dark:border-gray-700">
        <button type="button" onClick={() => navigate(`/projecten/${project.id}`)} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-control bg-accent/10 text-accent hover:bg-accent/20">
          <ExternalLink className="w-3.5 h-3.5" /> Open project
        </button>
      </div>
    </div>
  )
}
